// EntityFilterContext — the app's global "which entity am I currently looking
// at?" filter. Persisted per browser via localStorage so the choice survives
// reload/tab-open.
//
// - super_admin: 'all' + every entity in the entities table (fetched at mount).
// - single-entity user: hidden — the selector should not render at all.
// - multi-entity user (entity_id NULL, entities[] populated): 'all' + their
//   allowed entities.
//
// Callers pass selectedEntity as the `entity` query param on data fetches;
// when 'all' they omit it entirely, restoring the current combined view.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const STORAGE_KEY = 'ecp_selected_entity';

const EntityFilterContext = createContext(null);

export function EntityFilterProvider({ children }) {
  const { user } = useAuth();
  const [allEntities, setAllEntities] = useState([]); // fetched for super_admin
  const [selectedEntity, setSelectedEntityState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'all'; } catch { return 'all'; }
  });

  // Fetch entity catalogue once for super_admin — for others the allowed list
  // is already on the user object (entities[] JSONB).
  useEffect(() => {
    if (user?.role === 'super_admin') {
      api.get('/entities')
        .then((r) => setAllEntities(Array.isArray(r.data) ? r.data : []))
        .catch(() => setAllEntities([]));
    }
  }, [user?.role]);

  // Compute the user's allowed entities as {slug, name} rows.
  const allowedEntities = useMemo(() => {
    if (!user) return [];
    if (user.role === 'super_admin') {
      return allEntities.map((e) => ({ slug: e.slug, name: e.name }));
    }
    const slugs = Array.isArray(user.entities) ? user.entities : [];
    // Try to hydrate names from allEntities if we have them; else fall back to slug.
    const slugToName = new Map(allEntities.map((e) => [e.slug, e.name]));
    return slugs.map((s) => ({ slug: s, name: slugToName.get(s) || s }));
  }, [user, allEntities]);

  // Setter also writes to localStorage.
  const setSelectedEntity = useCallback((next) => {
    const v = next || 'all';
    setSelectedEntityState(v);
    try {
      if (v === 'all') localStorage.removeItem(STORAGE_KEY);
      else             localStorage.setItem(STORAGE_KEY, v);
    } catch { /* private-mode / storage disabled — non-fatal */ }
  }, []);

  // If the persisted selection isn't in the user's allowed set (e.g. someone
  // logs in as a different user), snap back to 'all'.
  useEffect(() => {
    if (!user) return;
    if (selectedEntity === 'all') return;
    const ok = allowedEntities.some((e) => e.slug === selectedEntity);
    if (!ok) setSelectedEntity('all');
  }, [user, allowedEntities, selectedEntity, setSelectedEntity]);

  // Show the dropdown only when the user actually has a choice.
  const showSelector = allowedEntities.length > 1;

  // Convenience: append entity param to a params object when a specific entity
  // is selected (skips it for 'all', matching current 'no param = combined view').
  const withEntity = useCallback((params = {}) => {
    if (selectedEntity && selectedEntity !== 'all') {
      return { ...params, entity: selectedEntity };
    }
    return params;
  }, [selectedEntity]);

  const value = { selectedEntity, setSelectedEntity, allowedEntities, showSelector, withEntity };
  return <EntityFilterContext.Provider value={value}>{children}</EntityFilterContext.Provider>;
}

export function useEntityFilter() {
  const ctx = useContext(EntityFilterContext);
  if (!ctx) {
    // Called outside a provider — return safe defaults so callers work in tests / storybook.
    return { selectedEntity: 'all', setSelectedEntity: () => {}, allowedEntities: [], showSelector: false, withEntity: (p) => p };
  }
  return ctx;
}
